import qs from 'qs';

import { PaymentsProviderNames, UnifiedAccountStatus } from '@invoice-simple/is-payments-sdk';
import { PaypalAccountSettings } from '@invoice-simple/is-paypal-sdk';
import { StripeAccountSettingsV2 } from '@invoice-simple/is-stripe-sdk';

import {
  AccountOrdersPaginated,
  CaptureStatus,
  PaypalOrderFormatted
} from '../components/PaymentsDashboard';
import { invokeIsPayments } from './apis/isPayments.api';
import { invokeIsPaypal } from './apis/paypal.api';
import { invokeIsStripe } from './apis/stripe.api';

const paypalApiV1Endpoint = '/api/v1';
const stripeApiV2Endpoint = '/api/v2';
const paymentsApiV1Endpoint = '/api/v1';

export interface AccountStatusOptions {
  /**
   * Skip the shared in-flight request. Use it right after a mutation, where a request that started
   * before the mutation landed would resolve to a stale status.
   */
  forceRefresh?: boolean;
}

/**
 * Shared so that the PayPal and Stripe contexts, which mount together and refetch together on
 * window focus, make one request between them instead of two. Cleared as soon as the request
 * settles, so the next mount or focus fetches again.
 *
 * This is request sharing, not caching: it only collapses callers that overlap in flight. Both
 * contexts are mounted in the same commit (`Root.tsx`), so their mount effects and their `focus`
 * listeners run in the same tick and always land inside that window.
 */
let inFlightAccountStatus: Promise<UnifiedAccountStatus> | null = null;

/**
 * Both provider statuses in one request, replacing the separate is-paypal `/accounts/me` and
 * is-stripe `/accounts/me` calls.
 */
export async function getUnifiedAccountStatus({
  forceRefresh = false
}: AccountStatusOptions = {}): Promise<UnifiedAccountStatus> {
  if (forceRefresh || !inFlightAccountStatus) {
    const request = invokeIsPayments<UnifiedAccountStatus>(
      'GET',
      `${paymentsApiV1Endpoint}/accounts/me/status`
    ).finally(() => {
      // Only the request that currently owns the slot may clear it. A `forceRefresh` caller can
      // replace the slot while this request is still in flight, and an unconditional clear would
      // then discard the *newer* request — leaving the next caller to start a third one.
      if (inFlightAccountStatus === request) {
        inFlightAccountStatus = null;
      }
    });

    inFlightAccountStatus = request;
  }

  return inFlightAccountStatus;
}

/**
 * `undefined` means is-payments omitted PayPal from an otherwise successful response because its
 * upstream was unavailable, so the status is *unknown* — it never means "not enabled". Callers must
 * keep their last known status rather than storing the absence.
 *
 * Indexes the provider key directly instead of using the SDK's `getPaypalStatus`, which collapses a
 * flag-*ineligible* provider to `undefined`. `flagEligibility` is a flag-audience verdict, not a
 * payability signal, so a live accepting merchant must still read as accepting.
 */
export async function getPaypalAccountStatus(
  options?: AccountStatusOptions
): Promise<PaypalAccountSettings | undefined> {
  const status = await getUnifiedAccountStatus(options);

  return status[PaymentsProviderNames.PAYPAL];
}

export async function paypalEnable(): Promise<PaypalAccountSettings> {
  return invokeIsPaypal('PUT', `${paypalApiV1Endpoint}/accounts/me/enable`);
}

export async function paypalDisable(): Promise<PaypalAccountSettings> {
  return invokeIsPaypal('PUT', `${paypalApiV1Endpoint}/accounts/me/disable`);
}

/** See `getPaypalAccountStatus` — `undefined` is "unknown", not "not enabled". */
export async function getStripeAccountStatus(
  options?: AccountStatusOptions
): Promise<StripeAccountSettingsV2 | undefined> {
  const status = await getUnifiedAccountStatus(options);

  return status[PaymentsProviderNames.STRIPE];
}

export async function stripeEnable(): Promise<StripeAccountSettingsV2> {
  return invokeIsStripe('PATCH', `${stripeApiV2Endpoint}/accounts/me/enable`);
}

export async function stripeDisable(): Promise<StripeAccountSettingsV2> {
  return invokeIsStripe('PATCH', `${stripeApiV2Endpoint}/accounts/me/disable`);
}

export async function stripePaymentsConnectClientSecret(): Promise<{ client_secret: string }> {
  return invokeIsStripe<{ client_secret: string }>(
    'GET',
    `${stripeApiV2Endpoint}/accounts/me/stripe-account-session`
  );
}

export async function getPaymentsOrders({
  sort = '-createdAt',
  page,
  limit,
  captureStatus = 'ALL'
}: {
  sort?: keyof PaypalOrderFormatted | `-${keyof PaypalOrderFormatted}`;
  captureStatus?: CaptureStatus | CaptureStatus[] | 'ALL';
  page?: number;
  limit?: number;
}): Promise<AccountOrdersPaginated> {
  const searchParams = qs.stringify({
    captureStatus:
      Array.isArray(captureStatus) || captureStatus === 'ALL' ? captureStatus : [captureStatus],
    sort,
    page: page,
    size: limit
  });

  return invokeIsPaypal('GET', `${paypalApiV1Endpoint}/accounts/me/orders?${searchParams}`);
}
